/**
 * Who sees this profile: members, therapists (grants), a new share code,
 * who looked, ownership transfer. Owner-only actions are shown only to the owner.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { Api, ApiError } from '@/core/api';
import { fa, formatJalali } from '@/core/jalali';
import { useProfile } from '@/app/ProfileShell';

const LEVEL_FA: Record<string, string> = {
  read: 'فقط دیدن',
  log: 'دیدن و ثبت تمرین',
  confirm: 'دیدن، ثبت و تأیید',
};

export function SharePage() {
  const { id } = useParams({ from: '/authed/p/$id' });
  const profile = useProfile();
  const qc = useQueryClient();
  const isOwner = profile.myLevel === 'owner';
  const isFamily = isOwner || profile.myLevel === 'member';
  const grants = useQuery({
    queryKey: ['grants', id],
    queryFn: () => Api.grants(id),
    enabled: isFamily,
  });
  const log = useQuery({
    queryKey: ['access-log', id],
    queryFn: () => Api.accessLog(id),
    enabled: isFamily,
  });
  const mapQ = useQuery({ queryKey: ['map'], queryFn: Api.map });

  const [label, setLabel] = useState('گفتاردرمانگر');
  const [level, setLevel] = useState<'read' | 'log' | 'confirm'>('confirm');
  const [branches, setBranches] = useState<string[]>([]);
  const [code, setCode] = useState<{ code: string; expiresAt: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [toUid, setToUid] = useState('');

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['grants', id] });
    void qc.invalidateQueries({ queryKey: ['profile', id] });
  };
  const make = useMutation({
    mutationFn: () => Api.shareCode(id, { level, branches, label }),
    onSuccess: (r) => {
      setCode(r);
      setErr(null);
      invalidate();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.code : 'خطا'),
  });
  const revoke = useMutation({
    mutationFn: (gid: string) => Api.revoke(id, gid),
    onSuccess: invalidate,
  });
  const transfer = useMutation({
    mutationFn: () => Api.transfer(id, { toUid: toUid.trim(), guardianLabel: 'سرپرست' }),
    onSuccess: () => {
      setErr(null);
      void qc.invalidateQueries({ queryKey: ['profile', id] });
    },
    onError: (e) => setErr(e instanceof ApiError ? e.code : 'خطا'),
  });

  if (!isFamily)
    return (
      <main className="page">
        <h2 className="page-title">دسترسی</h2>
        <section className="card">
          <p>
            شما از طریق کد اشتراک خانواده به این پروفایل دسترسی دارید. مدیریت دسترسی‌ها با خانواده
            است.
          </p>
        </section>
      </main>
    );

  return (
    <main className="page">
      <h2 className="page-title">چه کسی می‌بیند؟</h2>

      <section className="card">
        <div className="overline">خانواده</div>
        {profile.members.map((m) => (
          <div key={m.id} className="row spread">
            <span>{m.label ?? m.uid}</span>
            <span className="chip">
              {m.role === 'owner' ? 'مالک' : m.role === 'self' ? 'خودش' : 'سرپرست'}
            </span>
          </div>
        ))}
      </section>

      <section className="card">
        <div className="overline">درمانگرها</div>
        {grants.data?.filter((g) => g.active).length === 0 && (
          <p className="help">هنوز با درمانگری به اشتراک گذاشته نشده.</p>
        )}
        {grants.data
          ?.filter((g) => g.active)
          .map((g) => (
            <div key={g.id} className="row spread">
              <span>
                <strong>{g.label ?? 'درمانگر'}</strong>
                <br />
                <span className="help">
                  {LEVEL_FA[g.level]} ·{' '}
                  {g.branches.length === 0 ? 'همهٔ شاخه‌ها' : `${fa(g.branches.length)} شاخه`} · از{' '}
                  {formatJalali(g.startsAt)}
                </span>
              </span>
              {isOwner && (
                <button
                  type="button"
                  className="btn danger"
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate(g.id)}
                >
                  قطع
                </button>
              )}
            </div>
          ))}
        {grants.data?.some((g) => !g.active) && (
          <details>
            <summary className="help">دسترسی‌های پایان‌یافته</summary>
            {grants.data
              .filter((g) => !g.active)
              .map((g) => (
                <div key={g.id} className="help">
                  {g.label ?? 'درمانگر'} · تا {formatJalali(g.revokedAt ?? g.endsAt ?? g.startsAt)}
                </div>
              ))}
          </details>
        )}
      </section>

      {isOwner && (
        <section className="card">
          <div className="overline">اشتراک با درمانگر جدید</div>
          <p className="help">
            یک کد می‌سازید، به درمانگر می‌دهید؛ درمانگر در اپ خودش وارد می‌کند. هر وقت خواستید قطع
            می‌کنید.
          </p>
          <label className="field">
            <span>چه کسی؟</span>
            <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} />
          </label>
          <label className="field">
            <span>چه اجازه‌ای؟</span>
            <select
              className="input"
              value={level}
              onChange={(e) => setLevel(e.target.value as typeof level)}
            >
              {(['confirm', 'log', 'read'] as const).map((l) => (
                <option key={l} value={l}>
                  {LEVEL_FA[l]}
                </option>
              ))}
            </select>
          </label>
          <div className="field">
            <span>کدام شاخه‌ها؟ (هیچ‌کدام = همه)</span>
            <div className="row">
              {mapQ.data?.branches.map((b) => {
                const on = branches.includes(b.key);
                return (
                  <button
                    key={b.key}
                    type="button"
                    className={`btn ${on ? 'primary' : 'quiet'}`}
                    aria-pressed={on}
                    onClick={() =>
                      setBranches((s) => (on ? s.filter((k) => k !== b.key) : [...s, b.key]))
                    }
                  >
                    {b.title}
                  </button>
                );
              })}
            </div>
          </div>
          {err && <div className="error">{err}</div>}
          <button
            type="button"
            className="btn primary block"
            disabled={make.isPending}
            onClick={() => make.mutate()}
          >
            ساختن کد
          </button>
          {code && (
            <div className="notice" style={{ textAlign: 'center' }}>
              کد اشتراک:{' '}
              <strong className="machine" style={{ fontSize: 22, letterSpacing: 4 }}>
                {code.code}
              </strong>
              <br />
              <span className="help">
                تا {formatJalali(code.expiresAt)} معتبر است و یک بار مصرف می‌شود.
              </span>
            </div>
          )}
        </section>
      )}

      <section className="card">
        <div className="overline">چه کسی نگاه کرده</div>
        {log.data?.length === 0 && (
          <p className="help">هنوز کسی جز خانواده این پروفایل را ندیده.</p>
        )}
        {log.data?.slice(0, 20).map((l, i) => (
          <div key={i} className="help">
            {l.role === 'practitioner' ? 'درمانگر' : l.role} · {l.action} · {formatJalali(l.at)}
          </div>
        ))}
      </section>

      {isOwner && profile.ownerKind === 'guardian' && (
        <section className="card">
          <div className="overline">مالکیت</div>
          <p className="help">
            {profile.policy.selfAppDue
              ? `${profile.displayName} به سنی رسیده که می‌تواند پروفایل خودش را داشته باشد (${fa(Math.round(profile.policy.selfAppFromMonths / 12))} سال). شما همچنان همراه و ناظر می‌مانید.`
              : `از ${fa(Math.round(profile.policy.selfAppFromMonths / 12))} سالگی می‌توانید مالکیت را به خود ${profile.displayName} بدهید.`}
          </p>
          <label className="field">
            <span>شناسهٔ حساب خودش</span>
            <input
              className="input machine"
              value={toUid}
              onChange={(e) => setToUid(e.target.value)}
              placeholder="person_…"
            />
          </label>
          <button
            type="button"
            className="btn quiet block"
            disabled={toUid.trim().length < 3 || transfer.isPending}
            onClick={() => transfer.mutate()}
          >
            انتقال مالکیت
          </button>
        </section>
      )}

      <button
        type="button"
        className="btn quiet block"
        onClick={() =>
          void Api.exportProfile(id).then((data) => {
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `zoyaar-skills-${profile.displayName}.json`;
            a.click();
          })
        }
      >
        دانلود نسخهٔ کامل پروفایل
      </button>
    </main>
  );
}
